/* eslint-disable no-loop-func */
import pLimit from 'p-limit';

import * as contentTypes from '@/modules/content/store/types';
import * as templatesTypes from '@/modules/templates/store/types';
import * as rootTypes from '@/store/types';
// eslint-disable-next-line import/no-cycle
import campaignApi from '../api/campaignApi';
import * as types from './types';

const CONCURRENCY_LIMIT = 5;

async function updateGroupWithStats(campaignGroups, ordersMapped, concurrency = CONCURRENCY_LIMIT) {
  const keyToIndex = new Map(
    campaignGroups.map((grp, idx) => [`${grp.name}::${grp.advertiserId}`, idx]),
  );

  const limit = pLimit(concurrency);
  const limited = ([ids]) => limit(() => campaignApi.getGroupedStats(ids.join(',')));

  const tasks = campaignGroups.map((campaignGroup) => {
    const key = `${campaignGroup.name}::${campaignGroup.advertiserId}`;
    const campaignIds = campaignGroup.campaigns.map((item) => item.id);
    return limited([campaignIds]).then((data) => ({ key, data }));
  });

  const results = await Promise.all(tasks);

  for (const { key, data } of results) {
    const idx = keyToIndex.get(key);
    const group = campaignGroups[idx];
    if (group) {
      group.budgetRemaining = data.budgetRemaining;
      group.campaigns = group.campaigns.map((campaign) => {
        const {
          impressions, clicks, ctr, budgetRemaining,
        } = data.campaignStats[campaign.id] || {};
        const {
          startDate, endDate, currencyId, budget,
        } = ordersMapped[campaign.id];
        return {
          ...campaign,
          startDate,
          endDate,
          currencyId,
          budget,
          impressions: impressions || 0,
          clicks: clicks || 0,
          ctr: ctr ? Number(ctr).toFixed(2) : '%',
          budgetRemaining: budgetRemaining === null || budgetRemaining === undefined ? 100 : budgetRemaining,
        };
      });

      const totals = group.campaigns.reduce(
        (acc, item) => {
          acc.impressions += item.impressions;
          acc.clicks += item.clicks;
          acc.budget += item.budget;
          return acc;
        }, {
          impressions: 0, clicks: 0, budget: 0,
        },
      );

      group.impressions = totals.impressions;
      group.clicks = totals.clicks;
      group.budget = totals.budget;
      group.ctr = (totals.impressions && totals.clicks) ? Number((totals.clicks / totals.impressions) * 100).toFixed(2) : '';
    }
  }
}

function getCampaignGroups(apiCall) {
  return new Promise((resolve, reject) => apiCall
    .then(async (response) => {
      const { total, campaignGroups } = response;
      const campaignIds = campaignGroups.flatMap((group) => group.campaigns)
        .map((item) => item.id).join(',');

      if (campaignIds.length > 0) {
        const ordersMapped = campaignIds.length > 0 ? await campaignApi.getOrdersMapped(campaignIds.split(',')) : {};
        await updateGroupWithStats(campaignGroups, ordersMapped);
      }

      const payload = [campaignGroups, total];
      resolve(payload);
    })
    .catch((err) => {
      reject(err);
      return false;
    }));
}

const actions = {
  [types.FETCH_CAMPAIGNS]: (context, { params = {}, filters = {} }) => {
    if (!filters) return [];
    const {
      page, perPage, sortBy, sortDesc,
    } = params;

    const {
      search, states, advertiserId, accountManagerId,
    } = filters;
    const orderDirection = (sortDesc) ? 'DESC' : 'ASC';

    const queryParams = '?'.concat(
      page ? `&page=${page}&pageSize=${perPage}` : '',
      sortBy ? `&sortBy=${sortBy}&sortDirection=${orderDirection}` : '',
      search ? `&search=${encodeURIComponent(search)}` : '',
      states ? `&states=${states}` : '',
      advertiserId ? `&aid=${advertiserId}` : '',
      accountManagerId ? `&accountManagerId=${accountManagerId}` : '',
    );
    return campaignApi.getCampaigns(queryParams)
      .then((response) => response.campaigns);
  },
  [types.FETCH_ORDER]: (context, { advertiserId, campaignId, setOrder = false }) => campaignApi.getOrder(advertiserId, campaignId)
    .then((order) => {
      if (setOrder) context.commit(types.SET_ORDER, order);
      return order;
    }),
  [types.FETCH_ORDERS]: (context, { advertiserId, campaignIds }) => campaignApi.getOrders(advertiserId, campaignIds)
    .then((response) => {
      context.commit(types.SET_ORDERS, response);
      return response;
    }),
  [types.FETCH_CAMPAIGN_GROUPS]: (context, params) => {
    const {
      advertiserId, campaignType, search, customOnly,
    } = params;
    const queryParams = '?'.concat(
      advertiserId ? `&advertiserId=${advertiserId}` : '',
      campaignType ? `&campaignType=${campaignType}` : '',
      search ? `&search=${search}` : '',
      customOnly ? '&customOnly=true' : '',
    );

    return campaignApi.getCampaignGroups(queryParams)
      .then(async (response) => {
        const campaignGroups = response.groups.map((group) => ({ name: group }));
        context.commit(types.SET_CAMPAIGN_GROUPS, campaignGroups);
        return campaignGroups;
      });
  },
  [types.FETCH_CAMPAIGN_GROUPS_WITH_CAMPAIGNS]: (context, { params = {}, filters = {}, managed = true }) => {
    context.commit(types.SET_CAMPAIGN_GROUPS, []);
    const accountAdvertiserId = context.rootState.account.advertiserId;
    if (accountAdvertiserId === null) {
      return [[], 0];
    }

    const {
      page, perPage, sortBy, sortDesc, type,
    } = params;
    const {
      search, states, advertiserId, accountManagerId,
    } = filters;

    const orderDirection = (sortDesc) ? 'DESC' : 'ASC';

    let queryParams = '?'.concat(
      page ? `&page=${page}&pageSize=${perPage}` : '',
      sortBy ? `&sortBy=${sortBy}&sortDirection=${orderDirection}` : '',
      search ? `&search=${encodeURIComponent(search)}` : '',
      states ? `&states=${states}` : '',
      advertiserId ? `&aid=${advertiserId}` : '',
      accountManagerId ? `&accountManagerId=${accountManagerId}` : '',
    );

    let apiCall;

    if (managed) {
      queryParams = queryParams.concat(type ? `&type=${type}` : '');
      apiCall = campaignApi.getCampaignGroupsForManaged(queryParams);
    } else {
      apiCall = campaignApi.getSelfServiceCampaignGroups(queryParams);
    }

    return getCampaignGroups(apiCall);
  },
  [types.FETCH_SHARED_CAMPAIGN_GROUPS_WITH_CAMPAIGNS]: () => getCampaignGroups(campaignApi.getSharedCampaignGroups()),
  [types.FETCH_CAMPAIGNS_FOR_GROUP]: (context, payload) => campaignApi
    .getCampaignsForGroup(payload)
    .then((response) => response.campaigns),
  [types.FETCH_EXISTING_ADVERTISERS]: (context, managed) => {
    const queryParams = managed !== undefined ? `?managed=${managed}` : '';

    const checkCondition = (resolve, reject) => {
      campaignApi.getAdvertisers(queryParams)
        .then((response) => {
          if (response.length !== 0 && (!context.state.selectedAdvertiser.id)) {
            context.commit(types.SET_ADVERTISERS, response);
            if (managed) {
              context.commit(types.SET_SELECTED_ADVERTISER, response[0]);
            } else {
              const accountAdvertiser = response.find((advertiser) => advertiser.type === 'ACCOUNT');
              context.commit(types.SET_SELECTED_ADVERTISER, accountAdvertiser || response[0]);
            }
          }
          resolve(response);
        })
        .catch((err) => {
          context.commit(types.SET_IS_LOADING, false);
          reject(err);
        });
    };
    return new Promise(checkCondition);
  },
  [types.FETCH_ADVERTISER]: (context, advertiserId) => campaignApi
    .getAdvertiser(advertiserId ?? context.state.selectedAdvertiser.id)
    .then((advertiser) => {
      context.commit(types.SET_SELECTED_ADVERTISER, advertiser);
      return advertiser;
    })
    .catch(() => {
      context.commit(types.SET_IS_LOADING, false);
    }),
  [types.CREATE_ADVERTISER]: ({ commit }, advertiserName) => campaignApi
    .createAdvertiser(advertiserName)
    .then((advertiser) => {
      const newAdvertiser = [advertiser];
      commit(types.SET_ADVERTISERS, newAdvertiser);
      commit(types.SET_SELECTED_ADVERTISER, advertiser);
      return advertiser;
    })
    .catch(() => {
      commit(types.SET_IS_LOADING, false);
    }),
  [types.UPDATE_SELECTED_ADVERTISER]: (context, payload) => {
    context.commit(types.SET_SELECTED_ADVERTISER, payload);
  },
  [types.FETCH_EXISTING_CONTENT_PROVIDER]: (context) => {
    if (context.state.existingAdvertisers.length === 0) return false;
    const name = `${context.rootState.account.companyName}-${context.rootState.account.id}-${context.state.selectedAdvertiser.name}-${context.state.selectedAdvertiser.id}`;
    const checkCondition = (resolve, reject) => {
      campaignApi
        .getContentProvider(context.state.selectedAdvertiser.id)
        .then((response) => {
          if (response.length === 0) {
            const payload = {
              id: context.state.selectedAdvertiser.id,
              name,
              type: 'ADVERTISER',
            };
            campaignApi.createContentProvider(payload)
              .then((data) => {
                context.commit(types.SET_CONTENT_PROVIDER, data.id);
                context.commit(types.SET_CONTENT_PROVIDER_NAME, data.name);
                resolve(data);
              })
              .catch((err) => {
                reject(err);
                context.commit(types.SET_IS_LOADING, false);
              });
          } else {
            context.commit(types.SET_CONTENT_PROVIDER, response[0].id);
            context.commit(types.SET_CONTENT_PROVIDER_NAME, response[0].name);
            resolve(response);
            context.commit(types.SET_IS_LOADING, false);
          }
        })
        .catch((err) => {
          reject(err);
          context.commit(types.SET_IS_LOADING, false);
        });
    };
    return new Promise(checkCondition);
  },
  [types.CREATE_CAMPAIGN]: (context, payload) => {
    const campaignPayload = {
      advertiser: {
        id: context.state.selectedAdvertiser.id,
      },
      body: {
        name: payload.name,
        accountManagerUserId: payload.accountManagerUserId,
        salesUserId: payload.salesUserId,
        templateGroupId: payload.templateGroupId,
        group: payload.group ? payload.group : null,
      },
    };
    if (payload.agencyId) {
      campaignPayload.body.agencyId = payload.agencyId;
    }
    return campaignApi.createCampaign(campaignPayload)
      .then((response) => {
        context.commit(types.SET_CAMPAIGN, response);
        context.commit(`content/${contentTypes.SET_FEED_ID}`, response.feedId, { root: true });
        context.commit(types.ADD_OR_UPDATE_CAMPAIGN_PERMISSIONS, {
          campaignId: response.id,
          ownerAccess: true,
          shareTypes: [],
        });
        return response;
      });
  },
  [types.PATCH_CAMPAIGN]: ({ state, commit }, payload) => {
    const id = payload.id ? payload.id : state.campaign.id;
    const advertiserId = payload.advertiserId ? payload.advertiserId : state.campaign.advertiserId;
    return campaignApi.patchCampaign(advertiserId, id, payload)
      .then((campaign) => {
        commit(types.SET_CAMPAIGN, campaign);
        commit(types.SET_CAMPAIGN_STATE, campaign.state);
        return campaign;
      });
  },
  [types.COPY_CAMPAIGN]: ({ commit }, payload) => campaignApi
    .copyCampaign(payload)
    .then((campaign) => {
      commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: `${campaign.name} has been created`,
        type: 'info',
      }, { root: true });
      return campaign;
    }),
  [types.REMOVE_CAMPAIGN]: ({ commit }, payload) => campaignApi
    .setCampaignAsDeleted(payload)
    .then((campaign) => {
      commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: `${payload.campaignName} has been deleted`,
        type: 'warning',
      }, { root: true });
      return campaign;
    }),
  [types.FETCH_CURRENCIES]: (context) => {
    campaignApi.getCurrencies()
      .then((currencies) => {
        const currencyMap = Object.fromEntries(Object
          .entries(currencies)
          // eslint-disable-next-line no-unused-vars
          .map(([key, { code, symbolString }]) => [code, symbolString]));
        context.commit(rootTypes.SET_CURRENCY_MAP, currencyMap, { root: true });
        context.commit(types.SET_CURRENCIES, currencies);
        return currencies;
      }).catch(() => {
        context.commit(types.SET_CURRENCIES, [{ id: 2, name: 'US Dollar', code: 'USD' }]);
      });
  },
  [types.SET_CAMPAIGN_STATE]: (context, payload) => {
    if (payload.isSelfServiceDashboard) {
      return campaignApi
        .setCampaignStateForSelfServiceDashboard(payload)
        .then((campaign) => campaign);
    }
    return campaignApi
      .setCampaignState(payload)
      .then((campaign) => {
        context.commit(types.SET_CAMPAIGN_STATE, campaign);
        return campaign;
      });
  },
  [types.SET_CAMPAIGN_LIVE]: (context, payload) => campaignApi
    .setCampaignLive(payload)
    .then((response) => response),
  [types.GET_CAMPAIGN_BY_ID]: (context, payload) => {
    context.dispatch(types.RESET_CAMPAIGN);
    return campaignApi.getCampaignNameById(payload)
      .then((campaign) => {
        context.commit(`content/${contentTypes.SET_CREATIVE_MODE}`, campaign.shareMode, { root: true });
        context.commit(`content/${contentTypes.SET_FEED_ID}`, campaign.feedId, { root: true });
        context.commit(types.SET_CAMPAIGN, {
          id: payload,
          ...campaign,
        });
        return campaign;
      });
  },
  [types.GET_CAMPAIGN_BY_NAME]: (context, payload) => campaignApi
    .getCampaignByName(payload)
    .then((response) => response),
  [types.CREATE_ORDER]: (context, payload) => {
    const campaignId = context.state.campaign.id;
    const checkCondition = (resolve, reject) => {
      campaignApi.createOrder(campaignId, payload)
        .then((order) => {
          context.commit(types.SET_ORDER, order);
          resolve(order);
        })
        .catch((err) => {
          reject(err);
        });
    };
    return new Promise(checkCondition);
  },
  [types.PATCH_ORDER]: (context, payload) => campaignApi
    .patchOrder(context.state.campaign.id, context.state.order.id, payload)
    .then((order) => {
      context.dispatch(types.FETCH_ORDER, {
        advertiserId: context.state.campaign.advertiserId,
        campaignId: context.state.campaign.id,
        setOrder: true,
      });
      return order;
    }),
  [types.PATCH_BLACKLIST]: (context, payload) => campaignApi
    .patchBlacklist(context.state.campaign.id, payload)
    .then((blacklist) => {
      context.commit(types.SET_BLACKLIST, blacklist);
      return blacklist;
    }),
  [types.FETCH_BANNED_WORDS]: (context) => {
    campaignApi.getLinkingWords()
      .then((words) => {
        context.commit(types.SET_LINKING_WORDS, words);
        return words;
      });
  },
  [types.FETCH_COMMON_COUNTIES]: () => campaignApi.getCommonCounties()
    .then((counties) => counties),
  [types.FETCH_COUNTRIES]: (context) => {
    const checkCondition = (resolve, reject) => {
      campaignApi.getCountries()
        .then((response) => {
          const { countries, regions } = response;
          context.commit(types.SET_COUNTRIES, countries);
          context.commit(types.SET_REGIONS, regions);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        });
    };
    return new Promise(checkCondition);
  },
  [types.FETCH_GEO_LOCATIONS]: () => campaignApi.getGeoLocations()
    .then((response) => response),
  [types.FETCH_CAMPAIGN_STATES]: (context) => {
    const { states } = context.state;
    if (states && states.length > 0) return states;
    return campaignApi.getStates()
      .then((response) => {
        context.commit(types.SET_CAMPAIGN_STATES, response.data.states);
        return response.data.states;
      });
  },
  [types.DELETE_CAMPAIGN]: ({ state, commit }) => {
    if (state.campaign) {
      if (state.campaign.id) {
        campaignApi.deleteCampaign({
          advertiserId: state.selectedAdvertiser.id,
          campaignId: state.campaign.id,
        }).then(() => {
          commit(types.SET_CAMPAIGN, {});
        });
      }
    }
  },
  [types.DELETE_ORDER]: ({ state, commit }) => {
    if (state.order) {
      if (state.order.id) {
        campaignApi.deleteOrder({
          campaignId: state.campaign.id,
          orderId: state.order.id,
        }).then(() => {
          commit(types.SET_ORDER, {});
        });
      }
    }
  },
  [types.RESET_CAMPAIGN]: (context) => {
    context.commit(types.SET_CAMPAIGN, {});
    context.commit(types.SET_ORDER, {});
    context.commit(types.SET_BLACKLIST, {});
    context.commit(types.SET_SELECTED_ADVERTISER, {});
    context.commit(types.SET_DEAL_OVERRIDES, []);
    context.commit(types.CLEAR_PUBLISHERS);
    context.commit(types.SET_CAMPAIGN_INFO_CHANGES, {});
    context.commit(`content/${contentTypes.SET_CONTENT_SCRAPE_ID}`, 0, { root: true });
    context.commit(`content/${contentTypes.SET_SCRAPED_CONTENT}`, [], { root: true });
    context.commit(`content/${contentTypes.NEW_SET_SCRAPED_CONTENT}`, [], { root: true });
    context.commit(`content/${contentTypes.NEW_SET_CONTENT_ITEMS}`, [], { root: true });
    if (context.rootState.content.inProgressContentScrapes.totalStartLength > 0
      || context.rootState.content.inProgressContentScrapes.contentItems.length > 0) {
      context.commit(`content/${contentTypes.SET_KILL_CURRENT_SCRAPING_FETCH}`, true, { root: true });
    }
    context.commit(`content/${contentTypes.RESET_CONTENT_ITEM}`, null, { root: true });
    context.commit(`content/${contentTypes.SET_CREATIVE}`, { id: 0 }, { root: true });
    context.commit(`content/${contentTypes.SET_CREATIVES}`, [], { root: true });
    context.commit(`templates/${templatesTypes.UPDATE_AVAILABLE_TEMPLATES}`, [], { root: true });
    context.commit(`templates/${templatesTypes.SET_SELECTED_TEMPLATES}`, [], { root: true });
    context.commit(`content/${contentTypes.SET_ADD_UNITS_SELECTED}`, [], { root: true });
    context.commit(`content/${contentTypes.SET_CREATIVE_TYPE_SELECTED}`, null, { root: true });
  },
  [types.SUBMIT_CAMPAIGN]: (context) => campaignApi
    .submitCampaign(context.state.selectedAdvertiser.id, context.state.campaign.id)
    .then((response) => response)
    .catch((err) => err),
  [types.FETCH_EXISTING_CAMPAIGN]: (context, payload) => {
    const campaignPayload = {
      advertiserId: payload.advertiserId,
      campaignId: payload.campaignId,
    };
    return campaignApi.getCampaign(campaignPayload)
      .then((campaign) => {
        context.commit(types.SET_CAMPAIGN, campaign);
        context.commit(`content/${contentTypes.SET_FEED_ID}`, campaign.feedId, { root: true });
        return campaign;
      });
  },
  [types.UPDATE_PUBLISHERS_CREATIVES]: async (context, payload) => {
    const response = await campaignApi.patchPublishersCreative(payload);
    if (response.status !== 200) {
      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: 'We couldn\'t update the publishers\' creatives!',
        type: 'danger',
      }, { root: true });
    }
    return response;
  },
  [types.FETCH_EXISTING_BLACKLIST]: (context) => campaignApi
    .getBlacklist(context.state.campaign.id)
    .then((blacklist) => {
      context.commit(types.SET_BLACKLIST, blacklist);
      return blacklist;
    }),
  [types.FETCH_PUBLISHERS]: (context, payload) => campaignApi
    .getPublishers(payload)
    .then((publishers) => {
      context.commit(types.SET_PUBLISHER_LIST, publishers);
      return Promise.resolve(publishers);
    }),
  [types.FETCH_PUBLISHERS_GROUPS]: (context, type) => campaignApi
    .getPublisherGroups({ type })
    .then((groups) => groups.publisherGroups),
  [types.ADD_PUBLISHER]: ({ state, commit }, payload) => {
    if (state.publisherIds.indexOf(payload) === -1) {
      commit(types.SET_PUBLISHER, payload);
      return payload;
    }
  },
  [types.DELETE_PUBLISHER]: ({ state, commit }, payload) => {
    const itemToDeleteIndex = state.publisherIds.indexOf(payload);
    commit(types.REMOVE_PUBLISHER, itemToDeleteIndex);
  },
  [types.UPDATE_PUBLISHER_GROUP]: ({ commit }, payload) => {
    commit(types.SET_PUBLISHER_GROUPS, payload);
  },
  [types.FETCH_CREATIVE_SIZES]: (context) => campaignApi.getCreativeSizes()
    .then((templates) => {
      const templatesSortedBySize = templates.sort((a, b) => (a.width + a.height) - (b.width + b.height));
      context.commit(`content/${contentTypes.SET_CREATIVES_SIZES}`, templatesSortedBySize, { root: true });
      return templatesSortedBySize;
    }),
  [types.FETCH_PUBLISHER_DEALS]: () => campaignApi.getPublisherDeals()
    .then((deals) => deals),
  [types.FETCH_ADX_ADVERTISERS]: (context) => {
    campaignApi.getAdxAdvertisers()
      .then((advertisers) => {
        context.commit(types.SET_ADX_ADVERTISERS, advertisers);
      });
  },
  [types.FETCH_DEAL_OVERRIDES]: ({ state, commit }) => campaignApi
    .getDealOverrides({
      advertiserId: state.selectedAdvertiser.id,
      orderId: state.order.id,
    })
    .then(({ deals }) => {
      commit(types.SET_DEAL_OVERRIDES, deals.filter((o) => (o.override)));
    }),
  [types.UPDATE_DEAL_OVERRIDES]: ({ commit }, payload) => {
    commit(types.SET_DEAL_OVERRIDES, payload);
  },
  [types.CREATE_DEAL_OVERRIDES]: ({ state }) => {
    const creativeDeals = state.dealOverrides
      .map((deal) => ({
        publisherId: Number(deal.publisherId),
        creativeId: Number(deal.creativeId),
        dealIdString: deal.dealIdString.trim(),
      }));
    campaignApi.postCreativeDealOverrides({
      advertiserId: state.selectedAdvertiser.id,
      body: {
        orderId: state.order.id,
        deals: creativeDeals,
      },
    }).then((res) => (res));
  },
  [types.SUBMIT_DEAL_OVERRIDES]: ({ state }) => {
    const creativeDeals = state.dealOverrides
      .map((deal) => ({
        publisherId: Number(deal.publisherId),
        creativeId: Number(deal.creativeId),
        dealIdString: deal.dealIdString.trim(),
      }));
    return campaignApi.setCreativeDealOverrides({
      advertiserId: state.selectedAdvertiser.id,
      body: {
        orderId: state.order.id,
        deals: creativeDeals,
      },
    }).then((res) => (res));
  },
  [types.FETCH_AGENCIES]: (context) => campaignApi
    .getAgencies()
    .then((agencies) => {
      context.commit(types.SET_AGENCIES_LIST, agencies);
      return agencies;
    }),
  [types.CREATE_AGENCY]: ({ commit }, agencyName) => campaignApi
    .createAgency(agencyName)
    .then((agency) => {
      commit(types.ADD_AGENCY_TO_LIST, agency);
      return agency;
    })
    .catch(() => {
      commit(types.SET_IS_LOADING, false);
    }),
  [types.GET_PUBLISHERS_CREATIVES]: (context, payload) => campaignApi.getPublishersCreative(payload)
    .then((response) => response),
  [types.SET_VIEW_TYPE]: (context, payload) => {
    context.commit(types.SET_VIEW_TYPE, payload);
  },
  [types.SET_CAMPAIGN_INFO_CHANGES]: (context, payload) => {
    context.commit(types.SET_CAMPAIGN_INFO_CHANGES, payload);
  },
  [types.GET_CAMPAIGN_PROPERTIES]: (context, payload) => campaignApi.getCampaignProperties(payload)
    .then((response) => {
      context.commit(types.SET_CAMPAIGN_PROPERTIES, response.data.properties);
    }),
  [types.UPLOAD_LOGO_IMAGE]: (context, payload) => campaignApi.uploadCampaignLogo(payload)
    .then((response) => {
      context.commit(types.SET_CAMPAIGN_LOGO, response.logo);
      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: 'Master logo uploaded successfully',
        type: 'success',
      }, { root: true });
    }),
  [types.PATCH_CAMPAIGN_PROPERTIES]: (context, payload) => campaignApi.patchCampaignProperties(payload)
    .then((response) => {
      context.commit(types.SET_CAMPAIGN_PROPERTIES, response.properties);
    }),
  [types.DELETE_CAMPAIGN_LOGO]: (context, payload) => campaignApi.deleteLogo(payload)
    .then(() => {
      context.commit(types.DELETE_CAMPAIGN_LOGO);
      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: 'Master Logo deleted',
        type: 'success',
      }, { root: true });
    }),
  [types.FETCH_CAMPAIGN_ACCESS]: (context, campaignId) => campaignApi.getAccess(campaignId)
    .then((response) => response),
  [types.GET_ACCESS]: (context, payload) => campaignApi.getAccess(payload)
    .then((accessResponse) => {
      context.commit(types.ADD_OR_UPDATE_CAMPAIGN_PERMISSIONS, {
        campaignId: accessResponse.campaignId,
        ownerAccess: accessResponse.ownerAccess,
        shareTypes: accessResponse.shareTypes,
      });
      return accessResponse;
    }),
  [types.UPDATE_CAMPAIGN_GROUP]: (context, payload) => campaignApi
    .updateCampaignGroup(payload)
    .then(() => {
      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: `Group name "${payload.group}" has been changed to "${payload.data.group}"`,
        type: 'info',
      }, { root: true });
    }),
  [types.DELETE_CAMPAIGN_GROUP]: (context, payload) => campaignApi
    .deleteCampaignGroup(payload)
    .then(() => {
      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: `Campaigns have been removed from ${payload.group}`,
        type: 'warning',
      }, { root: true });
    }),
  [types.SET_DORMANT_CAMPAIGN_LIVE]: (context, payload) => campaignApi
    .setDormantCampaignLive(payload),
};
export default actions;
